// CME FedWatch (ENG-851) — request-side vocabulary and the preset → {from, to}
// derivation. Pure: no React, no network, so the range maths that feeds the query
// key is unit-testable on its own.
//
// The probabilities are derived from the 30-Day Fed Funds futures strip (ZQ) on
// GLBX. The ticket said SOFR, but CME's own tool prices off Fed Funds futures and
// that is what reproduces their published numbers; SOFR (SR1/SR3) is only the
// candlesticks, which need nothing from this module.
import { subDays } from 'date-fns'

import {
  calendarDateFromUtcDayMs,
  UTC,
  utcDayEndMs,
  utcDayMsFromCalendarDate,
  utcDayStartMs,
} from '@/lib/utils/utc-day'
import { Exchange } from '@/types/generated/common/types'
import type {
  FedwatchProbabilitiesRequest,
  FedwatchProbabilityList,
} from '@/types/generated/public_api'

const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/** Futures root the tree is built from; the server defaults to this when `symbol` is empty. */
export const FEDWATCH_SYMBOL = 'ZQ'

export const FEDWATCH_EXCHANGE = Exchange.EXCHANGE_GLBX

// ─── Views ────────────────────────────────────────────────────────────────────

export type FedwatchView = 'grid' | 'timeline'

export const FEDWATCH_VIEWS: FedwatchView[] = ['grid', 'timeline']

export function isFedwatchView(value: string): value is FedwatchView {
  return (FEDWATCH_VIEWS as string[]).includes(value)
}

// ─── Meeting date ─────────────────────────────────────────────────────────────

/**
 * A real calendar date in `YYYY-MM-DD`, not merely something shaped like one.
 * The regex alone accepts `2026-13-40` and `2026-02-31`, and this value drives the
 * row sort and the meeting timestamp, so it round-trips through Date to prove the
 * calendar actually contains it.
 */
export function isMeetingDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const ms = Date.parse(`${value}T00:00:00Z`)
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === value
}

// ─── Range ────────────────────────────────────────────────────────────────────

export { calendarDateFromUtcDayMs, utcDayMsFromCalendarDate } from '@/lib/utils/utc-day'

/**
 * Days the timeline opens with, counting both endpoints. Seven matches the server's
 * own default for an unset `from`.
 */
export const FEDWATCH_DEFAULT_RANGE_DAYS = 7

const MINUTE_BUCKET_MS = MINUTE_MS
const QUARTER_HOUR_BUCKET_MS = 15 * MINUTE_MS
const HOUR_BUCKET_MS = HOUR_MS
const DAY_BUCKET_MS = DAY_MS

/**
 * The bucket the server will downsample a span into: ≤24h → 1m, ≤7d → 15m,
 * ≤90d → 1h, beyond that 1d.
 *
 * Mirrored here only to size the query's `staleTime` — there is no point re-asking
 * inside a bucket that cannot have changed. It never shapes the request, so if the
 * server's tiers move, this goes stale in a way that costs a little extra polling
 * rather than wrong data.
 */
export function fedwatchBucketMsForSpan(spanMs: number): number {
  if (spanMs <= 24 * HOUR_MS) return MINUTE_BUCKET_MS
  if (spanMs <= 7 * DAY_MS) return QUARTER_HOUR_BUCKET_MS
  if (spanMs <= 90 * DAY_MS) return HOUR_BUCKET_MS
  return DAY_BUCKET_MS
}

export interface FedwatchRange {
  fromMs: number
  toMs: number
}

/**
 * The stored (possibly unset) range resolved to concrete, ordered bounds.
 *
 * `null` means "never chosen", not "epoch" — the timeline opens on the last
 * `FEDWATCH_DEFAULT_RANGE_DAYS` days and only pins a date once the user picks one.
 *
 * The end is the **last millisecond** of the chosen day, not its opening midnight.
 * Unlike the ETF feed, whose rows are themselves UTC days, this series is
 * timestamp-granular at up to one point per minute — sending the day's midnight as
 * `to` would silently cut the whole of that day out of the window.
 */
export function resolveFedwatchRange(
  fromDateMs: number | null,
  toDateMs: number | null,
  nowMs: number = Date.now()
): FedwatchRange {
  const todayStart = utcDayStartMs(nowMs)

  const rawToDay =
    Number.isFinite(toDateMs) && toDateMs !== null ? utcDayStartMs(toDateMs) : todayStart
  // Never past today: the producer only ever publishes up to the present minute.
  const toDayStart = Math.min(rawToDay, todayStart)

  const fromDayStart =
    Number.isFinite(fromDateMs) && fromDateMs !== null
      ? utcDayStartMs(fromDateMs)
      : subDays(toDayStart, FEDWATCH_DEFAULT_RANGE_DAYS - 1, { in: UTC }).getTime()

  // Clamped against the end, so an inverted pair collapses to a single day rather
  // than reaching the server as a range it would reject.
  return {
    fromMs: Math.min(fromDayStart, toDayStart),
    toMs: utcDayEndMs(toDayStart),
  }
}

// ─── Request builder ──────────────────────────────────────────────────────────

/**
 * The two request shapes we send, as a discriminated union so that `as_of` and
 * `from`/`to` together — which the server answers with a 400 — is unrepresentable
 * rather than merely discouraged.
 *
 * (The third shape the endpoint offers, `as_of` for comparing a meeting against
 * past instants, is deferred; it slots in here as a third arm.)
 */
export type FedwatchQuery =
  | { mode: 'grid' }
  | { mode: 'timeline'; meetingDate: string; from: string; to: string }

/**
 * Where the contract is encoded rather than remembered.
 *
 * - `limit` is never written: the handler ignores it, and sending it would imply a
 *   row cap that could slice a grid in half.
 * - `asOf` is always the empty array. The field is `repeated`, so ts-proto types it
 *   as a required `string[]`, and an empty one is wire-identical to omitting it —
 *   confirmed with backend that this takes the latest-grid path and does not trip
 *   the from/to mutual-exclusivity check.
 * - `symbol` is empty, which the server reads as ZQ.
 * - `to` is written explicitly on the timeline, never as `0`. Zero would mean the
 *   epoch here (the field is an `optional int64`), which is a 400 for an inverted
 *   range — the exact opposite of the sibling ETF Flow endpoint, whose plain
 *   `int64` reads zero as "now". Two endpoints, two meanings for one literal.
 *
 * The grid arm therefore sets neither `from` nor `to`, which matters more than it
 * looks: setting *either one* moves the request onto the range path, where
 * `meeting_date` becomes required — so a stray `to` would turn the all-meetings
 * grid into a 400.
 *
 * Returns `null` for a timeline query with no usable `meetingDate`, which is what
 * the query's `enabled` gates on.
 */
export function buildFedwatchRequest(
  query: FedwatchQuery,
  exchange: Exchange = FEDWATCH_EXCHANGE
): FedwatchProbabilitiesRequest | null {
  if (query.mode === 'grid') {
    return { exchange, symbol: '', asOf: [] }
  }

  if (!isMeetingDate(query.meetingDate)) return null

  return {
    exchange,
    symbol: '',
    asOf: [],
    meetingDate: query.meetingDate,
    from: query.from,
    to: query.to,
  }
}

// ─── Choosing between a pull result and a stream push ─────────────────────────

const maxTimestampMs = (list: FedwatchProbabilityList | undefined): number | null => {
  let max: number | null = null
  for (const item of list?.items ?? []) {
    const ms = Number(item.timestampMs)
    if (Number.isFinite(ms) && (max === null || ms > max)) max = ms
  }
  return max
}

/**
 * Picks whichever of the two carries the newer grid. Always a whole-grid
 * replacement — the two are never merged, because a FedWatch push is a full
 * replacement and row-level merging would accumulate stale vintages.
 *
 * Not a bare `stream ?? query`: a focus-triggered refetch lands a fresher pull that
 * `??` would hide behind an older push, pinning the panel to a stale grid until the
 * next message arrives.
 *
 * Three deliberate edge cases:
 * - An **empty stream list never wins**, whatever its timestamps. An empty `items`
 *   is what a filtered-out or malformed push degrades to, and blanking a good grid
 *   on one is the worst failure mode available here.
 * - A tie prefers the query result; same vintage, so don't churn identity.
 * - A list with no parseable timestamp cannot be shown to be newer, so it loses.
 */
export function pickNewerFedwatchList(
  streamList: FedwatchProbabilityList | undefined,
  queryList: FedwatchProbabilityList | undefined
): FedwatchProbabilityList | undefined {
  if (!streamList?.items.length) return queryList
  if (!queryList?.items.length) return streamList

  const streamMs = maxTimestampMs(streamList)
  const queryMs = maxTimestampMs(queryList)
  if (streamMs === null) return queryList
  if (queryMs === null) return streamList

  return streamMs > queryMs ? streamList : queryList
}
